// Filtrační panel pro StationSelectLayout: 11 filtrů (GH_ID, WSI, název, typ, veličiny, lokalita, výška, admin. dělení, povodí, aktivní)
import { useState, useMemo } from 'react'
import { Button, Separator } from '@heroui/react'
import { normalizeCzech, parseCoordinates } from '../../utils/geo'
import { useStationTypes, useBasins, useObsGroups, useElements, useStationHierarchy, useStationOwners } from '../../hooks/useAppData'
import { useNominatimSearch } from '../../hooks/useNominatimSearch'
import { HierarchyFilter } from './HierarchyFilter'
import { GroupCheckboxFilter } from './GroupCheckboxFilter'
import { useSavedGroupTrees } from '../../hooks/useSavedGroupTrees'
import { parseGhIdExpr } from '../../utils/ghIdExpr'
import { GroupProductFilterActions } from './GroupProductFilterActions'
import placeholderPinIcon from '../../assets/icons/placeholder-pin.svg'

const GH_ID_EXPR_HELP = `Vzor stejně jako dřív: % = libovolný řetězec, _ = jeden znak, bez nich = prefix.
Vzory lze kombinovat operátory AND, OR, NOT a závorkami.

Příklad:
(O1% OR O2%) AND NOT P7% AND %01
→ stanice začínající O1 nebo O2, nezačínající P7, končící 01`

const RESET_BTN_CLASS = 'text-gray-400 hover:text-gray-600 normal-case font-normal underline text-xs'

function FilterGroup({ title, children, defaultOpen = false, actions, onOpenChange }) {
  const [open, setOpen] = useState(defaultOpen)
  const toggle = () => setOpen(v => {
    const next = !v
    onOpenChange?.(next)
    return next
  })
  return (
    <div className="border-b border-gray-100 last:border-0">
      <div className="w-full flex items-center px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:bg-gray-50">
        <button
          className="flex-1 flex items-center gap-2 text-left"
          onClick={toggle}
        >
          {title}
          <span className="ml-auto">{open ? '▲' : '▼'}</span>
        </button>
        {actions && <div className="ml-1 shrink-0">{actions}</div>}
      </div>
      {open && <div className="px-3 pb-3 flex flex-col gap-1.5">{children}</div>}
    </div>
  )
}

function TextInput({ label, value, onChange, placeholder, help, error }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-xs text-gray-500 flex items-center gap-1">
        {label}
        {help && (
          <span className="text-gray-400 cursor-help" title={help}>ⓘ</span>
        )}
      </span>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={`border rounded-lg px-2 py-1 text-sm focus:outline-none ${error ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-blue-400'}`}
      />
      {error && <span className="text-xs text-red-600">{error}</span>}
    </label>
  )
}

function MultiCheckbox({ label, options, selected, onChange, hints = {} }) {
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val))
    else onChange([...selected, val])
  }
  return (
    <div>
      {label && <div className="text-xs text-gray-500 mb-1">{label}</div>}
      <div className="flex flex-col gap-0.5 max-h-36 overflow-y-auto">
        {options.map(opt => (
          <label key={opt} title={hints[opt]} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-gray-50 px-1 rounded">
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={() => toggle(opt)}
              className="accent-blue-500"
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  )
}

export function FiltersPanel({
  filters, onChange, onLocalityPickMode, localityPickMode,
  isAdmin, editingGroupId, hasProductFilter, groupProducts, activeProduct,
  onSaveToGroup, onSaveToProduct, onDeleteProductFilter,
  showGroupFilter = false,
}) {
  const stationTypes = useStationTypes()
  const hierarchy = useStationHierarchy()
  // Filtr „Skupina" je líný — stromy se staví (a dotazy běží) až po prvním rozbalení sekce, aby se
  // načítání /select nezdržovalo. Zůstává zapnutý i po zavření a taky když už jsou nějaké groupPaths.
  const [groupFilterActivated, setGroupFilterActivated] = useState(() => (filters.groupPaths?.length || 0) > 0)
  const savedGroupTrees = useSavedGroupTrees(showGroupFilter && groupFilterActivated)

  const basins = useBasins()
  const stationOwners = useStationOwners()
  const obsGroups = useObsGroups()
  const { elementsMap } = useElements()

  const set = (key, val) => onChange({ ...filters, [key]: val })
  const ghIdError = useMemo(() => parseGhIdExpr(filters.ghId).error, [filters.ghId])

  // +GH_ID / -GH_ID pole jsou defaultně schovaná; přepínají se tlačítky + / − v hlavičce
  // sekce Identifikátory. Pokud už mají hodnotu (např. z uloženého filtru), zobrazí se rovnou.
  const [showPlusGhId, setShowPlusGhId] = useState(() => !!filters.plusGhId)
  const [showMinusGhId, setShowMinusGhId] = useState(() => !!filters.minusGhId)

  const [localityText, setLocalityText] = useState('')
  const localityTextError = useMemo(() => {
    if (!localityText.trim()) return null
    return parseCoordinates(localityText) ? null : 'Nerozpoznaný formát souřadnic'
  }, [localityText])

  const handleLocalityTextChange = (val) => {
    setLocalityText(val)
    const parsed = parseCoordinates(val)
    if (parsed) {
      onChange({ ...filters, locality: parsed, localityRadius: filters.localityRadius || '50' })
    }
  }

  const [osmQuery, setOsmQuery] = useState('')
  const { results: osmResults, isLoading: osmLoading, error: osmError } = useNominatimSearch(osmQuery)

  const handleOsmSelect = (place) => {
    onChange({
      ...filters,
      osmLocality: { lat: parseFloat(place.lat), lon: parseFloat(place.lon), label: place.display_name },
      osmLocalityRadius: filters.osmLocalityRadius || '50',
    })
  }

  const handleReset = () => {
    onChange({
      ghId: '', wsi: '', name: '', fullName: '',
      stationTypes: [], elevationMin: '', elevationMax: '',
      hierarchyPaths: [], basin: [], stationOwners: [],
      elAbbreviations: [], elMatchMode: 'any', elCurrentOnly: false,
      activeOnly: true,
      locality: null, localityRadius: '',
      osmLocality: null, osmLocalityRadius: '',
      plusGhId: '', minusGhId: '',
      groupPaths: [],
    })
    setLocalityText('')
    setOsmQuery('')
    setShowPlusGhId(false)
    setShowMinusGhId(false)
  }

  return (
    <div className="flex flex-col h-full min-h-0 text-sm">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 shrink-0">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filtry</span>
        {isAdmin && editingGroupId && (
          <GroupProductFilterActions
            hasProductFilter={hasProductFilter}
            groupProducts={groupProducts}
            activeProduct={activeProduct}
            onSaveToGroup={onSaveToGroup}
            onSaveToProduct={onSaveToProduct}
            onDeleteProductFilter={onDeleteProductFilter}
          />
        )}
        <button className="text-xs text-gray-400 hover:text-gray-600 underline" onClick={handleReset}>
          Resetovat
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">

      {/* Active only */}
      <div className="px-3 py-2 border-b border-gray-100">
        <label className="flex items-center gap-2 cursor-pointer text-xs">
          <input
            type="checkbox"
            checked={filters.activeOnly}
            onChange={e => set('activeOnly', e.target.checked)}
            className="accent-blue-500"
          />
          <span className="font-medium">Jen současné stanice</span>
        </label>
      </div>

      {/* Skupina — stromový výběr podle uložených skupin stanic (jen výběr 1 stanice) */}
      {showGroupFilter && (
        <FilterGroup
          title="Skupina"
          defaultOpen={groupFilterActivated}
          onOpenChange={(open) => { if (open) setGroupFilterActivated(true) }}
          actions={(filters.groupPaths?.length > 0) && (
            <button className={RESET_BTN_CLASS} onClick={() => set('groupPaths', [])}>
              zrušit
            </button>
          )}
        >
          {!groupFilterActivated || savedGroupTrees.length === 0 ? (
            <div className="text-xs text-gray-400 px-1 py-1">
              {groupFilterActivated ? 'Žádné uložené skupiny.' : 'Načítám…'}
            </div>
          ) : (
            <GroupCheckboxFilter
              trees={savedGroupTrees}
              selected={filters.groupPaths || []}
              onChange={paths => set('groupPaths', paths)}
            />
          )}
        </FilterGroup>
      )}

      {/* GH_ID, WSI */}
      <FilterGroup
        title="Identifikátory"
        defaultOpen
        actions={
          <div className="flex items-center gap-1">
            <button
              title={showPlusGhId ? 'Skrýt +GH_ID' : 'Zobrazit +GH_ID (vždy zahrnout)'}
              onClick={() => setShowPlusGhId(v => {
                const next = !v
                if (!next && filters.plusGhId) set('plusGhId', '')
                return next
              })}
              className={`w-5 h-5 flex items-center justify-center rounded border text-sm leading-none transition-colors ${
                showPlusGhId ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white border-gray-300 text-gray-500 hover:border-blue-400'
              }`}
            >
              +
            </button>
            <button
              title={showMinusGhId ? 'Skrýt −GH_ID' : 'Zobrazit −GH_ID (vždy vyloučit)'}
              onClick={() => setShowMinusGhId(v => {
                const next = !v
                if (!next && filters.minusGhId) set('minusGhId', '')
                return next
              })}
              className={`w-5 h-5 flex items-center justify-center rounded border text-sm leading-none transition-colors ${
                showMinusGhId ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white border-gray-300 text-gray-500 hover:border-blue-400'
              }`}
            >
              −
            </button>
            {(filters.ghId || filters.wsi || filters.plusGhId || filters.minusGhId) && (
              <button
                className={RESET_BTN_CLASS}
                onClick={() => onChange({ ...filters, ghId: '', wsi: '', plusGhId: '', minusGhId: '' })}
              >
                zrušit
              </button>
            )}
          </div>
        }
      >
        <TextInput
          label="GH_ID"
          value={filters.ghId}
          onChange={v => set('ghId', v)}
          placeholder="Prefix, vzor s % a _, nebo výraz s AND/OR/NOT …"
          help={GH_ID_EXPR_HELP}
          error={ghIdError}
        />
        <TextInput label="WSI" value={filters.wsi} onChange={v => set('wsi', v)} placeholder="Prefix, nebo vzor s % a _ …" />
        {showPlusGhId && (
          <TextInput
            label="+GH_ID (vždy zahrnout)"
            value={filters.plusGhId}
            onChange={v => set('plusGhId', v)}
            placeholder="ID1, ID2, … — bez ohledu na ostatní filtry"
          />
        )}
        {showMinusGhId && (
          <TextInput
            label="-GH_ID (vždy vyloučit)"
            value={filters.minusGhId}
            onChange={v => set('minusGhId', v)}
            placeholder="ID1, ID2, … — bez ohledu na ostatní filtry"
          />
        )}
      </FilterGroup>

      {/* Name */}
      <FilterGroup
        title="Název"
        defaultOpen
        actions={(filters.name || filters.fullName) && (
          <button className={RESET_BTN_CLASS} onClick={() => onChange({ ...filters, name: '', fullName: '' })}>
            zrušit
          </button>
        )}
      >
        <TextInput label="Název (name)" value={filters.name} onChange={v => set('name', v)} placeholder="Fulltext (bez diakritiky)…" />
        <TextInput label="Úplný název (full_name)" value={filters.fullName} onChange={v => set('fullName', v)} placeholder="Fulltext…" />
      </FilterGroup>

      {/* Station type – toggle chips */}
      {stationTypes.length > 0 && (
        <FilterGroup
          title="Typ stanice"
          actions={filters.stationTypes.length > 0 && (
            <button className={RESET_BTN_CLASS} onClick={() => set('stationTypes', [])}>
              zrušit
            </button>
          )}
        >
          <div className="flex flex-wrap gap-1.5">
            {stationTypes.map(t => {
              const active = filters.stationTypes.includes(t.id)
              return (
                <button
                  key={t.id}
                  title={t.item}
                  onClick={() => {
                    const next = active
                      ? filters.stationTypes.filter(v => v !== t.id)
                      : [...filters.stationTypes, t.id]
                    set('stationTypes', next)
                  }}
                  className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                    active
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'
                  }`}
                >
                  {t.id}
                </button>
              )
            })}
          </div>
        </FilterGroup>
      )}

      {/* Measuring elements */}
      {Object.keys(obsGroups).length > 0 && (
        <FilterGroup
          title="Měřící veličiny"
          actions={filters.elAbbreviations.length > 0 && (
            <button
              className={RESET_BTN_CLASS}
              onClick={() => onChange({ ...filters, elAbbreviations: [], elMatchMode: 'any', elCurrentOnly: false })}
            >
              zrušit
            </button>
          )}
        >
          <div className="flex gap-3 mb-2">
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <input
                type="radio"
                name="elMatchMode"
                value="all"
                checked={filters.elMatchMode === 'all'}
                onChange={() => set('elMatchMode', 'all')}
                className="accent-blue-500"
              />
              Všechny zároveň
            </label>
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <input
                type="radio"
                name="elMatchMode"
                value="any"
                checked={filters.elMatchMode === 'any'}
                onChange={() => set('elMatchMode', 'any')}
                className="accent-blue-500"
              />
              Aspoň jedna
            </label>
          </div>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer mb-2">
            <input
              type="checkbox"
              checked={!!filters.elCurrentOnly}
              onChange={e => set('elCurrentOnly', e.target.checked)}
              className="accent-blue-500"
            />
            Jen současné měření
          </label>
          {Object.entries(obsGroups).map(([obsType, abbrs]) => {
            const groupSelected = filters.elAbbreviations.some(([t]) => t === obsType)
            return (
            <div key={obsType}>
              <div className="flex items-center gap-1.5 mb-0.5">
                <div className="text-xs font-semibold text-gray-500">{obsType}</div>
                {groupSelected && (
                  <button
                    className={RESET_BTN_CLASS}
                    onClick={() => set('elAbbreviations', filters.elAbbreviations.filter(([t]) => t !== obsType))}
                  >
                    zrušit
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-0.5 max-h-36 overflow-y-auto">
                {abbrs.map(abbr => {
                  const isSelected = filters.elAbbreviations.some(([t, a]) => t === obsType && a === abbr)
                  const toggle = () => {
                    if (isSelected)
                      set('elAbbreviations', filters.elAbbreviations.filter(([t, a]) => !(t === obsType && a === abbr)))
                    else
                      set('elAbbreviations', [...filters.elAbbreviations, [obsType, abbr]])
                  }
                  return (
                    <label key={abbr} title={elementsMap[abbr]?.name} className="flex items-center gap-1.5 text-xs cursor-pointer hover:bg-gray-50 px-1 rounded">
                      <input type="checkbox" checked={isSelected} onChange={toggle} className="accent-blue-500" />
                      {abbr}
                    </label>
                  )
                })}
              </div>
            </div>
            )
          })}
        </FilterGroup>
      )}

      {/* Locality */}
      <FilterGroup
        title="Lokalita (bod + poloměr)"
        actions={(filters.locality || filters.localityRadius) && (
          <button
            className={RESET_BTN_CLASS}
            onClick={() => {
              onChange({ ...filters, locality: null, localityRadius: '' })
              setLocalityText('')
            }}
          >
            zrušit
          </button>
        )}
      >
        <div className="flex items-end gap-1.5">
          <div className="flex-1 min-w-0">
            <TextInput
              label="Zadej souřadnice"
              value={localityText}
              onChange={handleLocalityTextChange}
              placeholder="50.123, 14.456"
              error={localityTextError}
              help="Desetinné stupně, stupně+minuty nebo stupně/minuty/vteřiny (např. 50°7'23&quot;N 14°27'22&quot;E). Volitelně s N/S/E/W."
            />
          </div>
          <button
            title={localityPickMode ? 'Klikni do mapy…' : 'Zadat bod v mapě'}
            className={`shrink-0 p-1.5 rounded-lg border transition-colors ${localityPickMode ? 'bg-blue-100 border-blue-400' : 'border-gray-200 hover:border-blue-300'}`}
            onClick={() => onLocalityPickMode && onLocalityPickMode(!localityPickMode)}
          >
            <img src={placeholderPinIcon} className="w-4 h-4 shrink-0" alt="Zadat bod v mapě" />
          </button>
        </div>
        {filters.locality && (
          <div className="text-xs text-gray-500">
            Bod: {filters.locality.lat.toFixed(4)}°N, {filters.locality.lon.toFixed(4)}°E
          </div>
        )}
        <TextInput
          label="Poloměr [km]"
          value={filters.localityRadius}
          onChange={v => set('localityRadius', v)}
          placeholder="napr. 50"
        />
      </FilterGroup>

      {/* Locality via OpenStreetMap (Nominatim) */}
      <FilterGroup
        title="Lokalita podle OSM"
        actions={(filters.osmLocality || filters.osmLocalityRadius) && (
          <button
            className={RESET_BTN_CLASS}
            onClick={() => {
              onChange({ ...filters, osmLocality: null, osmLocalityRadius: '' })
              setOsmQuery('')
            }}
          >
            zrušit
          </button>
        )}
      >
        <TextInput
          label="Poloměr [km]"
          value={filters.osmLocalityRadius}
          onChange={v => set('osmLocalityRadius', v)}
          placeholder="napr. 50"
        />
        <TextInput
          label="Hledat místo"
          value={osmQuery}
          onChange={setOsmQuery}
          placeholder="Např. Praha, Krkonoše, Vltava…"
          error={osmError}
          help="Vyhledávání přes OpenStreetMap Nominatim"
        />
        {osmLoading && <div className="text-xs text-gray-400">Hledám…</div>}
        {osmResults.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {osmResults.map(place => {
              const active = filters.osmLocality?.label === place.display_name
              return (
                <button
                  key={place.place_id}
                  title={place.display_name}
                  onClick={() => handleOsmSelect(place)}
                  className={`text-xs px-2 py-0.5 rounded-full border transition-colors max-w-full truncate ${
                    active
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'
                  }`}
                >
                  {place.display_name}
                </button>
              )
            })}
          </div>
        )}
        {filters.osmLocality && (
          <div className="text-xs text-gray-500" title={filters.osmLocality.label}>
            {filters.osmLocality.label}
            <br />
            {filters.osmLocality.lat.toFixed(4)}°N, {filters.osmLocality.lon.toFixed(4)}°E
          </div>
        )}
      </FilterGroup>

      {/* Elevation */}
      <FilterGroup
        title="Nadmořská výška [m]"
        actions={(filters.elevationMin || filters.elevationMax) && (
          <button
            className={RESET_BTN_CLASS}
            onClick={() => onChange({ ...filters, elevationMin: '', elevationMax: '' })}
          >
            zrušit
          </button>
        )}
      >
        <div className="flex items-center gap-1">
          <div className="flex flex-col gap-0.5 flex-1">
            <span className="text-xs text-gray-500">Od</span>
            <input
              type="number"
              value={filters.elevationMin}
              onChange={e => set('elevationMin', e.target.value)}
              placeholder="0"
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm w-full focus:outline-none focus:border-blue-400"
            />
          </div>
          <span className="text-gray-400 mt-4">–</span>
          <div className="flex flex-col gap-0.5 flex-1">
            <span className="text-xs text-gray-500">Do</span>
            <input
              type="number"
              value={filters.elevationMax}
              onChange={e => set('elevationMax', e.target.value)}
              placeholder="2000"
              className="border border-gray-200 rounded-lg px-2 py-1 text-sm w-full focus:outline-none focus:border-blue-400"
            />
          </div>
        </div>
      </FilterGroup>

      {/* Administrative hierarchy */}
      {hierarchy.length > 0 && (
        <FilterGroup
          title="Administrativní dělení (strom)"
          actions={(filters.hierarchyPaths || []).length > 0 && (
            <button className={RESET_BTN_CLASS} onClick={() => onChange({ ...filters, hierarchyPaths: [] })}>
              zrušit
            </button>
          )}
        >
          <HierarchyFilter
            hierarchy={hierarchy}
            selected={filters.hierarchyPaths || []}
            onChange={(paths) => {
              onChange({ ...filters, hierarchyPaths: paths })
            }}
          />
        </FilterGroup>
      )}

      {/* Basin */}
      {basins.length > 0 && (
        <FilterGroup
          title="Povodí (basin)"
          actions={filters.basin.length > 0 && (
            <button className={RESET_BTN_CLASS} onClick={() => set('basin', [])}>
              zrušit
            </button>
          )}
        >
          <MultiCheckbox options={basins} selected={filters.basin} onChange={v => set('basin', v)} />
        </FilterGroup>
      )}

      {/* Station owner (geography.sow_name) */}
      {stationOwners.length > 0 && (
        <FilterGroup
          title="Vlastník stanice"
          actions={(filters.stationOwners?.length || 0) > 0 && (
            <button className={RESET_BTN_CLASS} onClick={() => set('stationOwners', [])}>
              zrušit
            </button>
          )}
        >
          <MultiCheckbox
            options={stationOwners}
            selected={filters.stationOwners || []}
            onChange={v => set('stationOwners', v)}
          />
        </FilterGroup>
      )}
      </div>
    </div>
  )
}
